// 1. Const arrays + derived union types (NO TypeScript `enum` — erasableSyntaxOnly forbids it)
export const JOURNEY_TYPES = ["ivf", "iui"] as const;
export type JourneyType = (typeof JOURNEY_TYPES)[number];

export const TASK_CATEGORIES = ["clinical", "medication", "cycle"] as const;
export type TaskCategory = (typeof TASK_CATEGORIES)[number];

export const STEP_STATUSES = ["completed", "current", "future"] as const;
export type StepStatus = (typeof STEP_STATUSES)[number];

export const TASK_STATUSES = ["ongoing", "completed"] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

export const TASK_SOURCES = ["clinic", "patient"] as const;
export type TaskSource = (typeof TASK_SOURCES)[number];

// 2. Leaf interfaces
export interface StepHighlight {
	dayCount: string; // e.g., "10–14 days" (legacy fallback)
	monitoringSchedule: string; // e.g., "Every 2-3 days"
	staffInfo: string; // e.g., "Dr. Smith + nursing team"
	headsUpNote: string; // wellness note for Phase 41 "Heads Up" card
	// Phase 44 follow-up additions for Figma calendar/compass row composition
	milestone?: string; // e.g., "Treatment Plan Received, est. Week 2"
	currentDayLabel?: string; // e.g., "Day [N] of ~14" — UI replaces [N] with computed day
	estimatedLengthDays?: number; // total days for "Day N of ~total" rendering
	monitoringFrequency?: string; // legacy — superseded by milestone in V2 fixture
	nextEvent?: string; // legacy — superseded by milestone in V2 fixture
}

export interface JourneyTask {
	id: string;
	title: string;
	description: string;
	category: TaskCategory; // drives Phase 42 colored top border
	deadline: string; // ISO date string OR human-readable label
	status: TaskStatus;
	progress?: number; // 0-100, optional
	source: TaskSource; // mirrors v1.11 events `source` precedent
	iconName?: string; // Phase 44: action category key for icon resolution ("fill" | "upload" | "book" | "review-sign" | "test" | "notify" | "monitor")
	subStepId?: string; // Phase 44: links task to JourneyStep.subSteps[].id for grouping
	subStepTitle?: string; // Phase 44: cheap-render header label for grouped CurrentTasks
	progressTotal?: number; // Phase 44: total items for Fill tasks (e.g., 8 fields)
	progressUnit?: string; // Phase 44: unit label for progress (e.g., "fields", "questions")
}

export interface FAQ {
	id: string;
	question: string;
	answer: string;
}

export interface JourneySubStep {
	id: string;
	title: string;
	description?: string;
}

// 3. Container interfaces
export interface JourneyStep {
	id: string;
	order: number; // 1-based, drives sort + Phase 40 auto-scroll target
	title: string;
	description: string; // CONT-01 description text
	estimatedDateRange: string; // CONT-01 date range
	estimatedStartDate?: string; // Phase 44 follow-up: ISO date for "Day N of total" calc
	status: StepStatus;
	subSteps: JourneySubStep[];
	highlight: StepHighlight;
	tasks: JourneyTask[];
	faqs: FAQ[];
	askEvePrompt: string; // ACT-01 step-specific pre-filled chat question
}

export interface Journey {
	id: string;
	type: JourneyType;
	name: string;
	treatmentType: string; // display string, e.g., "IVF Treatment"
	totalSteps: number;
	steps: JourneyStep[];
}
